import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { IBuilding } from 'src/app/building-managment/building-management-interfaces';
import { FilterFloorsComponent } from 'src/app/floor-management/filter-floors/filter-floors.component';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { IFilterFloor } from 'src/app/shared/state-management/state-interfaces';
import { getFullName } from 'src/app/shared/utils/full-name.util';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { PopupProfile } from '../profile-management.service';

export interface AssignUnit {
  unitId: string;
  unitName: string;
  label: string;
  floorId: string;
}

export interface AssignFloor {
  floorId: string;
  floorName: string;
  wingName?: string;
  units: AssignUnit[];
}

const byName = (a?: string, b?: string) =>
  (a || '').localeCompare(b || '', undefined, { numeric: true });

@Component({
  selector: 'app-profile-assign-unit',
  templateUrl: './profile-assign-unit.component.html',
  styleUrls: ['./profile-assign-unit.component.scss'],
})
export class ProfileAssignUnitComponent implements OnChanges {
  @Input() profileDetails!: PopupProfile;
  @Input() building?: IBuilding;
  @Input() floors: AssignFloor[] = [];
  @Input() dialogRef?: { close: () => void };
  @Input() stepper = 21;
  @Output() stepperChange = new EventEmitter<number>();
  @Input() selectedUnits: AssignUnit[] = [];
  @Output() selectedUnitsChange = new EventEmitter<AssignUnit[]>();
  searchTerm = '';
  filter!: IFilterFloor;
  filterCount = 0;
  visibleFloors: AssignFloor[] = [];

  constructor(private helperService: HelperService) {}

  get userName(): string {
    const p = this.profileDetails;
    return getFullName(p?.firstName, p?.middleName, p?.lastName);
  }

  get hasUnits(): boolean {
    return this.floors?.some(floor => floor.units.length > 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.floors) return;
    this.resetFilter();
    this.applyFilters();
  }

  search(term: string | String): void {
    this.searchTerm = String(term || '');
    this.applyFilters();
  }

  openFilter(): void {
    this.helperService
      .openModalDialog(FilterFloorsComponent, this.filter, [CONSTANTS.FIXED_SCREEN_MODAL])
      ?.afterClosed()
      .subscribe((filter: IFilterFloor | undefined) => {
        if (filter) {
          this.filter = filter;
          this.filterCount = filter.floorsSelected.length + filter.wingsSelected.length;
        } else {
          this.resetFilter();
        }
        this.applyFilters();
      });
  }

  continue(): void {
    if (this.selectedUnits?.length) this.stepperChange.emit(22);
  }

  isSelected(unit: AssignUnit): boolean {
    return this.selectedUnits?.some(selected => selected.unitId === unit.unitId);
  }

  toggleUnit(unit: AssignUnit): void {
    const current = this.selectedUnits || [];
    this.selectedUnits = this.isSelected(unit)
      ? current.filter(selected => selected.unitId !== unit.unitId)
      : [...current, unit];
    this.selectedUnitsChange.emit(this.selectedUnits);
  }

  back(): void {
    this.stepperChange.emit(20);
  }

  close(): void {
    this.dialogRef?.close();
  }

  private applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    const { floorsSelected = [], wingsSelected = [] } = this.filter || {};
    this.visibleFloors = [...(this.floors || [])]
      .filter(floor =>
        (!floorsSelected.length || floorsSelected.includes(floor.floorName)) &&
        (!wingsSelected.length || wingsSelected.includes(floor.wingName as string)))
      .sort((a, b) => byName(a.floorName, b.floorName))
      .map(floor => ({
        ...floor,
        units: floor.units
          .filter(unit => !term || unit.label.toLowerCase().includes(term))
          .sort((a, b) => byName(a.unitName, b.unitName)),
      }))
      .filter(floor => floor.units.length > 0);
  }

  // Mirrors the add-user building listing filter setup.
  private resetFilter(): void {
    const floors = (this.floors || []).map(floor => ({
      floorId: floor.floorId,
      floorName: floor.floorName,
      wingName: floor.wingName,
      accessPointsCount: 0,
      unitsCount: floor.units.length,
      floorState: CONSTANTS.NOT_SELECTED,
      wingState: CONSTANTS.NOT_SELECTED,
    }));
    let emptyWingAdded = false;
    const wings = floors
      .map(floor => floor.wingName)
      .filter((wing, index, all) => {
        if (!emptyWingAdded && (wing === '' || wing === null)) {
          emptyWingAdded = true;
          return true;
        }
        return !!wing && all.indexOf(wing) === index;
      })
      .map(wingName => ({ wingName: wingName as string, wingState: CONSTANTS.NOT_SELECTED }));
    this.filter = {
      floors,
      wings,
      floorsSelected: [],
      wingsSelected: [],
      filterType: CONSTANTS.BUILDING_LISTING,
    };
    this.filterCount = 0;
  }
}
